import { __, sprintf } from "@wordpress/i18n";
import { useMemo, useState } from "@wordpress/element";
import { SelectControl, TextareaControl, ToggleControl } from "@wordpress/components";
import { AJAX_ACTION, ENUM, NONCE, OPTIONS, USERS } from "../../utils/constants";
import { Button } from "../../components/button";
import { ajax } from "../../config/axios";
import { toast } from "react-toastify";
import { css } from "@/utils/css";
import { SwitchBox } from "@/components/switch";
import { TextareaInput } from "@/components/textarea-input";
import { Select } from "@/components/select";
import { useUsageStats } from "@/hooks/use-usage-stats";
import { IS_PRO } from "@/utils/constants";
import { SimpleProAlert } from "@/components/pro-alert-box";

export default function TopicResponseSection() {
    const [submitting, setSubmitting] = useState(false);
    const [selectedUser, setSelectedUser] = useState(OPTIONS.AI_AUTHOR_ID);
    const users = useMemo(
        () =>
            USERS?.map((user) => ({
                label: user.name,
                value: `${user.id}`,
            })),
        []
    );
    const [disclaimer, setDisclaimer] = useState(OPTIONS.AI_RESPONSE_NOTICE);
    const [responseEnabled, setResponseEnabled] = useState(OPTIONS.AI_RESPONSE_TO_TOPIC);
    const [responseAsReply, setResponseAsReply] = useState(OPTIONS.AI_RESPONSE_AS_REPLY);

    const { data: usageStats } = useUsageStats();

    const handleSubmit = async () => {
        setSubmitting(true);

        try {
            await ajax({
                nonce: NONCE.TOPIC_RESPONSE_PREFERENCES,
                action: AJAX_ACTION.TOPIC_RESPONSE_PREFERENCES,
                [ENUM.AI_RESPONSE_TO_TOPIC]: Number(responseEnabled),
                [ENUM.AI_RESPONSE_AS_REPLY]: Number(responseAsReply),
                [ENUM.AI_RESPONSE_NOTICE]: disclaimer,
                [ENUM.AI_AUTHOR_ID]: selectedUser,
            });

            toast.success(__("Settings saved successfully", "antimanual"));
        } catch (e: any) {
            toast.error(e?.message || __("Something went wrong", "antimanual"));
        }

        setSubmitting(false);
    };

    return (
        <>
            <div className={css("ai-answer-grid")}>
                <div className={css("p-8")}>
                    <div className={css("flex justify-between items-start mb-5")}>
                        <SwitchBox
                            label={__("AI Response to Topics", "antimanual")}
                            help={__("If enabled, Antimanual will generate a response when a topic is created.", "antimanual")}
                            checked={responseEnabled}
                            onChange={(value) => setResponseEnabled(value)}
                        />
                         {!IS_PRO && usageStats?.forum_answer && (
                            <SimpleProAlert>
                                {sprintf(
                                    __("Usage: %1$d / %2$d", "antimanual"),
                                    usageStats.forum_answer.count,
                                    usageStats.forum_answer.limit
                                )}
                            </SimpleProAlert>
                        )}
                    </div>
                    <input type="hidden" name={ENUM.AI_RESPONSE_TO_TOPIC} value={Number(responseEnabled)} />
                    {responseEnabled ? (
                        <TextareaInput
                            label={__("AI Response Disclaimer", "antimanual")}
                            help={__(
                                "This disclaimer will be shown above the AI-generated response to inform users that the following reply was generated by AI and may not always be fully accurate.",
                                "antimanual"
                            )}
                            value={disclaimer || ""}
                            onChange={(value) => setDisclaimer(value)}
                            className={css("mb-6 block")}
                        />
                    ) : null}
                    <Button className={"mt-6"} loading={submitting} type="button" onClick={handleSubmit}>
                        {__("Save Changes", "antimanual")}
                    </Button>
                </div>
                <div className={css("max-w-xl p-8")}>
                    {responseEnabled ? (
                        <>
                            <SwitchBox
                                className={css("mb-5")}
                                label={__("Response As Reply", "antimanual")}
                                help={__("If enabled, The response will be included as a reply to the topic.", "antimanual")}
                                checked={responseAsReply}
                                onChange={(value) => setResponseAsReply(value)}
                            />
                            <input type="hidden" name={ENUM.AI_RESPONSE_AS_REPLY} value={Number(responseAsReply)} />
                            {responseAsReply ? (
                                <Select
                                    label={__("AI Author", "antimanual")}
                                    help={__(
                                        "Select the AI author account that will be used to post the AI-generated response. Only Administrator, Editor, and Author accounts are allowed to be selected as the AI Author.",
                                        "antimanual"
                                    )}
                                    value={selectedUser}
                                    onChange={(value) => setSelectedUser(value)}
                                    options={users || []}
                                />
                            ) : null}
                        </>
                    ) : null}
                </div>
            </div>
        </>
    );
}
